import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Button from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import Spinner from '@atlaskit/spinner';
import { view } from '@forge/bridge';

import { ValueLabel } from '../../../components/config/options-adder/interfaces';
import { useIsLicenseActive } from '../../../hooks/useIsLicenseActive';
import useMultilevelFields from '../../../hooks/useMlvFields';
import { hasValidObjectLength } from '../../../utils/levelModelUtils';
import { LicenseBanner } from '../../../utils/LicenseBanner';
import GroupingSelect from '../components/GroupingSelect';
import { DEFAULT_CONFIG } from '../constants';
import { GadgetConfig, GadgetContext } from '../model';
import { ConfigWrapper } from '../styled';

import ConfigEmptyState from './ConfigEmptyState';
import { JqlField } from './JqlField';
import MultilevelFieldSelect from './MultilevelFieldSelect';
import cacheClient from '../GadgetCacheClient';

const Configuration = () => {
    const [config, setConfig] = useState<GadgetConfig>(DEFAULT_CONFIG);
    const [fieldOptions, isLoading] = useMultilevelFields(field => ({ label: field.name, value: field.id }) as ValueLabel);
    const [isJqlValid, setIsJqlValid] = useState(true);
    const { isLicenseActive } = useIsLicenseActive();

    const onFieldChange = useCallback((fieldId: string) => setConfig(prevState => ({ ...prevState, fieldId })), []);

    const onGroupingChange = useCallback(
        (groupingFieldId: string) => setConfig(prevState => ({ ...prevState, groupingFieldId })),
        []
    );

    const onEmptyValuesChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) =>
            setConfig(prevState => ({
                ...prevState,
                hideEmptyValues: event.target.checked,
            })),
        []
    );

    const onSave = async() => {
        const context = (await view.getContext()) as GadgetContext;
        await cacheClient.delete(context);
        await view.submit(config);
    };

    const onJqlChange = useCallback((jql: string, isValid: boolean) => {
        setConfig(prevState => ({ ...prevState, jql }));
        setIsJqlValid(isValid);
    }, []);

    useEffect(() => {
        void view.getContext().then(value => {
            const currentConfig = value.extension.gadgetConfiguration as GadgetConfig;
            const configToSet = hasValidObjectLength(currentConfig) ? currentConfig : DEFAULT_CONFIG;
            setConfig(configToSet);
        });
    }, []);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <LicenseBanner isLicenseValid={isLicenseActive}>
            {fieldOptions.length ?
                <ConfigWrapper>
                    <MultilevelFieldSelect
                        value={config.fieldId}
                        onChange={onFieldChange}
                        options={fieldOptions}
                    />
                    <JqlField
                        value={config.jql}
                        onChange={onJqlChange}
                        label='Filter using JQL'
                    />
                    <GroupingSelect
                        value={config.groupingFieldId}
                        onChange={onGroupingChange}
                        label='Group by'
                    />
                    <Checkbox
                        isChecked={config.hideEmptyValues}
                        label='Hide levels with no issues'
                        onChange={onEmptyValuesChange}
                        testId='empty-values-checkbox'
                    />
                    <div>
                        <Button
                            onClick={onSave}
                            isDisabled={!isJqlValid || !config.fieldId}
                        >
                            Show results
                        </Button>
                    </div>
                </ConfigWrapper>
            :   <ConfigEmptyState />}
        </LicenseBanner>
    );
};

export default Configuration;
